/**
 * ViewerSidebar - AI Chat Panel for Lightning Q&A
 * Uses Gemini Flash for fast, cost-effective responses
 */

import React, { useState, useCallback } from 'react';
import {
  Drawer,
  Box,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Bolt as BoltIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { API_ENDPOINTS } from '../../config/api';
import { QuickQAResponse } from './types';

interface ViewerSidebarProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  currentPage: number;
  onNavigateToPage: (page: number) => void;
}

const ViewerSidebar: React.FC<ViewerSidebarProps> = ({
  open,
  onClose,
  documentId,
  currentPage,
  onNavigateToPage
}) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState<Array<{ page: number; citation: string; blockId?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [timeMs, setTimeMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const askQuestion = useCallback(async () => {
    if (!question.trim() || loading) return;

    setLoading(true);
    setError(null);
    const start = Date.now();

    try {
      const API_KEY = process.env.REACT_APP_API_KEY || 'psagot-api-key-2024-secure';
      const response = await fetch(API_ENDPOINTS.v3QuickQA, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
        },
        body: JSON.stringify({
          document_id: documentId,
          question: question.trim(),
          current_page: currentPage
        })
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const data: QuickQAResponse = await response.json();
      setAnswer(data.answer);
      setSources(data.sources || []);
      setTimeMs(Date.now() - start);
    } catch (err) {
      console.error('Quick Q&A error:', err);
      setError(err instanceof Error ? err.message : 'Failed to get answer');
    } finally {
      setLoading(false);
    }
  }, [question, documentId, currentPage, loading]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      askQuestion();
    }
  };

  const copyAnswer = async () => {
    try {
      await navigator.clipboard.writeText(answer);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 380 },
          maxWidth: '100%'
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: '#1A2341',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BoltIcon sx={{ color: '#FFD700' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Lightning Q&A
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Question Input */}
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder="Ask about this document..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={askQuestion}
                    disabled={loading || !question.trim()}
                    color="primary"
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <BoltIcon sx={{ color: '#FFD700' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Press Enter to send, Shift+Enter for new line
          </Typography>
        </Box>

        <Divider />

        {/* Answer Area */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {error ? (
            <Typography color="error">{error}</Typography>
          ) : answer ? (
            <>
              <Typography
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  mb: 2
                }}
              >
                {answer}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* Sources */}
              {sources.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Sources
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {sources.map((source, i) => (
                      <Chip
                        key={i}
                        label={`Page ${source.page} ${source.citation}`}
                        onClick={() => onNavigateToPage(source.page)}
                        clickable
                        size="small"
                        sx={{
                          bgcolor: '#E3F2FD',
                          '&:hover': { bgcolor: '#BBDEFB' }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary'
              }}
            >
              <Typography>Ask a question about this document</Typography>
            </Box>
          )}
        </Box>

        {/* Footer with timing and copy */}
        {answer && (
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {timeMs}ms
            </Typography>
            <IconButton size="small" onClick={copyAnswer} title="Copy answer">
              <CopyIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ViewerSidebar;
